import ButtonBasic from "@rentiohq/shared-frontend/dist/components/components/ButtonBasic";
import Spacer, {
  ESpacerWeight,
} from "@rentiohq/shared-frontend/dist/components/components/Spacer";
import verifyPhoneSchemas from "@rentiohq/shared-frontend/dist/forms/verifyPhone";
import {
  isPerformingSubmitActionSelector,
  performingSubmitActionErrorSelector,
  performingSubmitActionResultSelector,
  submitActionCreator,
} from "@rentiohq/shared-frontend/dist/forms/verifyPhone/schema.verifyPhone.actions";
import { useCountDown } from "@rentiohq/shared-frontend/dist/hooks/useCountDown";
import * as authActions from "@rentiohq/shared-frontend/dist/redux/auth/auth.actions";
import * as authSelectors from "@rentiohq/shared-frontend/dist/redux/auth/auth.selectors";
import { createForm } from "@rentiohq/shared-frontend/dist/redux/form/form.actions";
import { generateFormId } from "@rentiohq/shared-frontend/dist/redux/form/form.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { formatPhone } from "@rentiohq/shared-frontend/dist/utils/phone/phone.utils";
import { htmlTextAsReactText } from "@rentiohq/shared-frontend/dist/utils/text/text.utils";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { Navigate } from "react-router-dom";
import { IRootState } from "redux/reducers";
import FormMultistep from "../../../../components/FormMultistep";
import InfoHeader from "../../../../components/InfoHeader";
import Page from "../../../../components/Page";
import { getReferrer } from "../../../../utils/navigation.utils";
import * as S from "./VerifyPhone.styles";

const COUNT_LIMIT /* in milliseconds */ = 60_000;

export const VerifyPhone = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const { remainingCountDownInSeconds, startCountdown } =
    useCountDown(COUNT_LIMIT);

  const user = useSelector((state: IRootState) => authSelectors.getUser(state));

  const [formId] = React.useState(generateFormId());
  const [preventRedirect, setPreventRedirect] = React.useState(false);

  React.useEffect(() => {
    dispatch(
      createForm({
        formId,
        schemas: verifyPhoneSchemas({}),
        withSummary: false,
      }),
    );
  }, []);

  // Event handlers
  const handleCustomSuccessAction = () => {
    setPreventRedirect(false);
  };

  const handlePressSignIn = () => {
    dispatch({ type: "LOG_OUT" });

    navigate("/auth/signin", {
      state: { referrer: getReferrer(location) },
    });
  };

  // Helpers
  const getPhone = () => {
    if (!user?.unverifiedPhone) {
      return;
    }

    return formatPhone(user.unverifiedPhone);
  };

  const handleResendPhone = async () => {
    startCountdown();
    dispatch(authActions.signUpResendVerificationSms());
  };

  // Render
  const renderRedirect = () => {
    if (preventRedirect) {
      return null;
    }

    if (!user || user.unverifiedPhone) {
      return null;
    }

    return <Navigate to={getReferrer(location)} />;
  };

  const renderExtraTrailingControls = () => (
    <>
      <Spacer weight={ESpacerWeight.W16} />
      <S.ButtonInfo>
        <span>{getLocalizedText("verify_sms.cta.resend_sms.disclosure")} </span>
        <ButtonBasic
          title={`${getLocalizedText("verify_sms.cta.resend_sms")}${
            remainingCountDownInSeconds > 0
              ? ` (${remainingCountDownInSeconds})`
              : ""
          }`}
          onPress={handleResendPhone}
          disabled={remainingCountDownInSeconds > 0}
        />
      </S.ButtonInfo>

      <Spacer weight={ESpacerWeight.W16} />

      <S.ButtonInfo>
        <span>{getLocalizedText("auth.sign_up.login.title")} </span>
        <ButtonBasic
          title={getLocalizedText("auth.sign_up.login.cta")}
          onPress={handlePressSignIn}
        />
      </S.ButtonInfo>
    </>
  );

  return (
    <>
      {renderRedirect()}
      <Page>
        <InfoHeader
          title={getLocalizedText("auth.verification.title")}
          body={htmlTextAsReactText(
            getLocalizedText("system.verification.message.new", {
              phone: getPhone() || "",
            }),
          )}
        />
        <S.FormWrap>
          <FormMultistep
            formId={formId}
            // @ts-ignore state match
            isPerformingSubmitActionSelector={isPerformingSubmitActionSelector()}
            // @ts-ignore state match
            performingSubmitActionResultSelector={performingSubmitActionResultSelector()}
            // @ts-ignore state match
            performingSubmitActionErrorSelector={performingSubmitActionErrorSelector()}
            submitActionCreator={submitActionCreator()}
            customSuccessAction={handleCustomSuccessAction}
            submitTitle={getLocalizedText("auth.verification.submit.label")}
          />
          {renderExtraTrailingControls()}
        </S.FormWrap>
      </Page>
    </>
  );
};

// eslint-disable-next-line import/no-default-export
export default VerifyPhone;
